import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: "top" | "bottom" | "left" | "right";
  delay?: number;
  className?: string;
  disabled?: boolean;
}

export function Tooltip({
  content,
  children,
  side = "top",
  delay = 120,
  className,
  disabled = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 10;
    let top = 0;
    let left = 0;

    if (side === "right") {
      top = rect.top + rect.height / 2;
      left = rect.right + gap;
    } else if (side === "left") {
      top = rect.top + rect.height / 2;
      left = rect.left - gap;
    } else if (side === "top") {
      top = rect.top - gap;
      left = rect.left + rect.width / 2;
    } else if (side === "bottom") {
      top = rect.bottom + gap;
      left = rect.left + rect.width / 2;
    }

    setCoords({ top, left });
  };

  const show = () => {
    if (disabled || !content) return;
    updatePosition();
    timeoutRef.current = setTimeout(() => {
      updatePosition();
      setIsVisible(true);
    }, delay);
  };

  const hide = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (!isVisible) return;
    const handleScrollOrResize = () => {
      hide();
    };
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const transformStyle = {
    right: "translateY(-50%)",
    left: "translate(-100%, -50%)",
    top: "translate(-50%, -100%)",
    bottom: "translateX(-50%)",
  }[side];

  return (
    <div
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {isVisible &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              transform: transformStyle,
              zIndex: 9999,
            }}
            className={cn(
              "pointer-events-none whitespace-nowrap rounded-md bg-zinc-900/95 dark:bg-zinc-800/95 text-zinc-100 text-[11px] font-semibold px-2.5 py-1 shadow-xl border border-white/10 dark:border-white/10 backdrop-blur-xs select-none transition-opacity animate-in fade-in zoom-in-95 duration-100",
              className
            )}
          >
            {content}
          </div>,
          document.body
        )}
    </div>
  );
}
